import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Swords, Timer, Trophy, Zap, Loader2, ToggleLeft, UsersRound } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { useAiJob, isStopped } from "@/lib/aiJob";
import { useAuth } from "@/context/AuthContext";
import { PaidNote } from "@/components/Paywall";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { logWarn } from "@/lib/log";

const KINDS = { live: ["Live quiz race", Zap], truefalse: ["True/false speed round", ToggleLeft], team: ["Team battle", UsersRound],
  duel: ["Flashcard duel", Swords], async: ["48h challenge", Timer] };
const COUNTS = { truefalse: [5, 8, 10, 15] };
const HINT = { live: "Everyone races the same questions, 20s each.", truefalse: "Quick-fire statements, 10 seconds each. Fastest right answer scores most.",
  team: "Players are split into two teams as they join. Team scores add up.", duel: "Challenge one member. First correct answer takes the point.",
  async: "Play any time in the next 48 hours, at your own pace." };
const STATUS = { lobby: "Lobby open", invited: "Waiting for opponent", active: "In progress", finished: "Finished", expired: "Expired" };

function NewGame({ group, onCreated }) {
  const { user, refreshEnt } = useAuth();
  const [f, setF] = useState({ kind: "live", topic: "", count: 6, use_notes: false, invitee_id: "" });
  const [busy, setBusy] = useState(false);
  const others = group.members.filter((m) => m.user_id !== user.user_id);
  const job = useAiJob({ onStopped: refreshEnt });
  const create = async () => {
    if (busy) { job.stop(); setBusy(false); return; }
    setBusy(true);
    try { const { data } = await job.run((o) => http.post(`/groups/${group.class_id}/games`, f, o)); refreshEnt(); onCreated(data.game_id); }
    catch (e) { if (!isStopped(e) && e.response?.status !== 402) toast.error(formatError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-[#121725] p-5 space-y-3" data-testid="new-game-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {Object.entries(KINDS).map(([k, [label, Icon]]) => (
          <button key={k} onClick={() => setF({ ...f, kind: k, count: (COUNTS[k] || [5, 6, 8, 10]).includes(f.count) ? f.count : (COUNTS[k] || [5, 6, 8, 10])[1] })} data-testid={`game-kind-${k}`}
            aria-pressed={f.kind === k}
            className={`p-3 rounded-2xl border text-left flex items-start gap-3 transition-colors ${f.kind === k ? "border-sky-400/60 bg-sky-400/10" : "border-white/10 hover:bg-white/5"}`}>
            <span className="h-8 w-8 rounded-xl bg-sky-400/15 flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-sky-300" /></span>
            <span className="min-w-0"><span className="block text-sm font-medium">{label}</span><span className="block text-xs text-neutral-400 mt-0.5 leading-snug">{HINT[k]}</span></span>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Input value={f.topic} onChange={(e) => setF({ ...f, topic: e.target.value })} placeholder="Topic, e.g. Photosynthesis" className="flex-1 min-w-[200px] bg-white/5 border-white/10" data-testid="game-topic-input" />
        <Select value={String(f.count)} onValueChange={(v) => setF({ ...f, count: Number(v) })}>
          <SelectTrigger className="w-[130px] bg-white/5 border-white/10" data-testid="game-count-select"><SelectValue /></SelectTrigger>
          <SelectContent>{(COUNTS[f.kind] || [5, 6, 8, 10]).map((n) => <SelectItem key={n} value={String(n)}>{n} questions</SelectItem>)}</SelectContent>
        </Select>
        {f.kind === "duel" && (
          <Select value={f.invitee_id} onValueChange={(v) => setF({ ...f, invitee_id: v })}>
            <SelectTrigger className="w-[180px] bg-white/5 border-white/10" data-testid="game-invitee-select"><SelectValue placeholder="Challenge who?" /></SelectTrigger>
            <SelectContent>{others.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>
      <p className="text-xs text-neutral-500" data-testid="game-kind-hint">{HINT[f.kind]}</p>
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-neutral-400"><Checkbox checked={f.use_notes} onCheckedChange={(v) => setF({ ...f, use_notes: !!v })} data-testid="game-use-notes" /> Use this group's notes</label>
        <button onClick={create} className="px-5 py-2 rounded-full bg-sky-400 text-white text-sm font-medium hover:bg-sky-500 disabled:opacity-50 flex items-center gap-2" data-testid="game-create-btn">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} {busy ? "Stop" : "Host game · 1 message"}
        </button>
      </div>
    </div>
  );
}

function OpenLabel({ g, invited }) {
  if (invited) return "Accept duel";
  if (g.status === "finished") return <span className="flex items-center gap-1"><Trophy className="h-3 w-3" />Results</span>;
  return g.joined ? "Open" : "Join";
}

export function GroupGames({ group }) {
  const { user, ent } = useAuth();
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const load = useCallback(() => http.get(`/groups/${group.class_id}/games`).then((r) => setGames(r.data))
    .catch((e) => logWarn("Couldn't refresh games", e)), [group.class_id]);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  const open = async (g) => {
    try {
      if (!g.joined && ["lobby", "invited", "active"].includes(g.status)) await http.post(`/games/${g.game_id}/join`);
      navigate(`/app/games/${g.game_id}`);
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="space-y-4" data-testid="group-games">
      {ent?.features.groups ? <NewGame group={group} onCreated={(id) => navigate(`/app/games/${id}`)} />
        : <PaidNote text="Join and play any game here. Hosting games is on paid plans." testid="game-host-locked" />}
      {!games.length && <p className="text-sm text-neutral-500">No games yet. Host the first one!</p>}
      {games.map((g) => {
        const [label, Icon] = KINDS[g.kind];
        const invited = g.kind === "duel" && g.status === "invited" && g.invitee_id === user.user_id;
        return (
          <div key={g.game_id} className="rounded-xl border border-white/10 bg-white/[0.02] p-4 flex items-center gap-3" data-testid={`game-row-${g.game_id}`}>
            <Icon className="h-5 w-5 text-sky-300 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{label} · {g.topic}</p>
              <p className="text-xs text-neutral-500">{g.host_name} · {g.players} playing · {STATUS[g.status]}{g.leader && g.status === "finished" ? ` · winner ${g.leader}` : ""}</p>
            </div>
            {g.status !== "expired" && (
              <button onClick={() => open(g)} className={`text-xs px-3 py-1.5 rounded-full ${invited ? "bg-yellow-300 text-black" : "border border-white/15 hover:bg-white/5"}`} data-testid={`game-open-${g.game_id}`}>
                <OpenLabel g={g} invited={invited} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
